/**
 * Extended Onboarding API client
 *
 * Handles communication with the extended onboarding endpoints for
 * preference collection via character image selection.
 */
import apiClient from './client';

export interface OnboardingImage {
  character_id: number;
  image_url: string;
  character_name: string;
  attribute_values: Record<string, any>;
}

export interface OnboardingStep {
  step_number: number;
  title: string;
  description: string;
  images: OnboardingImage[];
  focus_attributes: string[];
}

export interface OnboardingConfig {
  enabled: boolean;
  steps: number;
  images_per_step: number;
}

export interface OnboardingStatus {
  extended_onboarding_enabled: boolean;
  onboarding_completed: boolean;
  onboarding_skipped: boolean;
  current_step: number;
  total_steps: number;
}

export interface SelectionRequest {
  step: number;
  selected_character_ids: number[];
  shown_character_ids: number[];  // All character IDs shown in this step (to prevent duplicates)
  skipped: boolean;
}

export interface SelectionResponse {
  success: boolean;
  step: number;
  selected_count: number;
  profile_updated: boolean;
}

export interface CompleteResponse {
  success: boolean;
  preferences_extracted: boolean;
  message: string;
}

export const extendedOnboardingApi = {
  /**
   * Get the extended onboarding configuration
   */
  getConfig: async (): Promise<OnboardingConfig> => {
    const response = await apiClient.get<OnboardingConfig>('/onboarding/extended/config');
    return response.data;
  },

  /**
   * Get the current user's extended onboarding status
   */
  getStatus: async (): Promise<OnboardingStatus> => {
    const response = await apiClient.get<OnboardingStatus>('/onboarding/extended/status');
    return response.data;
  },

  /**
   * Get all onboarding steps with character images
   * Gender filter is automatically determined from user profile (gender + sexual orientation)
   * @param language - Language for localized labels ('de', 'en')
   */
  getSteps: async (language = 'de'): Promise<OnboardingStep[]> => {
    const response = await apiClient.get<OnboardingStep[]>('/onboarding/extended/steps', {
      params: { language }
    });
    return response.data;
  },

  /**
   * Get a specific onboarding step
   * Gender filter is automatically determined from user profile
   * @param stepNumber - The step number (1-based)
   * @param language - Language for localized labels
   */
  getStep: async (stepNumber: number, language = 'de'): Promise<OnboardingStep> => {
    const response = await apiClient.get<OnboardingStep>(`/onboarding/extended/step/${stepNumber}`, {
      params: { language }
    });
    return response.data;
  },

  /**
   * Save a user's selection for an onboarding step
   * @param selection - The selection data
   */
  saveSelection: async (selection: SelectionRequest): Promise<SelectionResponse> => {
    const response = await apiClient.post<SelectionResponse>('/onboarding/extended/selection', selection);
    return response.data;
  },

  /**
   * Complete the extended onboarding and extract preferences
   */
  complete: async (): Promise<CompleteResponse> => {
    const response = await apiClient.post<CompleteResponse>('/onboarding/extended/complete');
    return response.data;
  },

  /**
   * Skip the extended onboarding entirely
   */
  skip: async (): Promise<CompleteResponse> => {
    const response = await apiClient.post<CompleteResponse>('/onboarding/extended/skip');
    return response.data;
  }
};

export default extendedOnboardingApi;
